/**
 * A study's items as the builder offers them a file: the one owner, read by
 * the backend's write and opening chain (`concept-assets.ts`, `goals.ts`) and
 * by the builder's cards.
 *
 * - The display name the write keeps for an asset (`assetDisplayNameOnWrite`)
 *   and whether assets are price versions of one product
 *   (`arePriceVersionsOfOneProduct`), which decide what one item is, and
 *   the price version a question is about (`ownPriceVersion`, D9), which
 *   the compiler, the builder's title chips and the chat reply name with
 *   its price.
 * - The items a file can go on (`attachItemsOf`, F19): one per product, never
 *   one per price version.
 * - What a file or a link handed to the builder on its own can become
 *   (`materialOffers`), from the family registry's typed facts.
 */

import type {
  ChatMessageCollect,
  ChatMessageCollectAttachItem,
  ProductAssetPrice,
  TestedAssetType,
} from "./index";
import { productPriceStructureIssues } from "./accepted-artifact-validator";
import { DEFAULT_MAX_ASSET_NAME_LENGTH } from "./asset-description-limits";
import { assetRefToken } from "./asset-ref-tokens";
import { assetTextReachesRespondents } from "./asset-text-provenance";
import {
  ASSET_FAMILY_FACTS,
  EXPOSURE_BOUND_FAMILIES,
  FILES_PER_ASSET,
  isAssetFamily,
  LINK_MATERIAL_FAMILIES,
  MEDIA_MATERIAL_FAMILIES,
  TESTED_ASSET_MAX_BY_TYPE,
} from "./asset-families";
import {
  formatProductPrice,
  formatTypedPrice,
  type PriceWording,
  productVersionLabel,
} from "./product-version-label";
import { type CapabilityId, capabilityFileOnCard } from "./capabilities";
import { recipeDeclarations } from "./study-spec";

const MAX_PERSISTED_ASSET_NAME_LENGTH = 255;
const MIN_ASSET_NAME_LENGTH = 8;
const ELLIPSIS = "…";

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** The twin-facing label: the minted ordinal ("Ad 1"), or else the name. */
export function twinFacingAssetLabel(asset: {
  name?: unknown;
  twinFacingLabel?: unknown;
}): string {
  return cleanText(asset.twinFacingLabel) || cleanText(asset.name);
}

type RespondentLabelAsset = {
  name?: unknown;
  type?: unknown;
  messageText?: unknown;
  twinFacingLabel?: unknown;
};

function assetFamilyOf(asset: { type?: unknown }) {
  return isAssetFamily(asset.type) ? ASSET_FAMILY_FACTS[asset.type] : undefined;
}

function assetField(
  asset: RespondentLabelAsset,
  field: "name" | "messageText",
): string {
  return cleanText(asset[field]);
}

/**
 * Whether respondents meet this asset by an ordinal ("Ad 2", "Option 2"): the
 * one per-asset rule. An ordinal family (ad, landing page, message) always
 * does; an exposure-bound family read by its name (a concept, product or
 * pack) does when that name is not one a respondent may read
 * (`assetTextReachesRespondents`: a placeholder, or a name no writer recorded).
 * Brands and pains, and a family read by its copy, never do. The ordinal
 * assigner mints a `twinFacingLabel` exactly when this holds, and every
 * respondent reader keys on that label, never on the family.
 */
export function assetHasOrdinal(asset: {
  type?: unknown;
  metadata?: unknown;
}): boolean {
  const family = assetFamilyOf(asset);
  if (!family) return false;
  return (
    family.respondentProse === "ordinal" ||
    (family.respondentProse === "name" &&
      family.exposureBound &&
      !assetTextReachesRespondents(asset, "name"))
  );
}

/**
 * How respondent prose (a title, a description, an answer's or a row's
 * description, a free-text row label, lane copy) names an asset, in every
 * phase: its minted ordinal when it has one (`assetHasOrdinal`), else the
 * family registry's `respondentProse` field.
 */
export function respondentProseAssetLabel(asset: RespondentLabelAsset): string {
  const ordinal = cleanText(asset.twinFacingLabel);
  if (ordinal) return ordinal;
  return assetFamilyOf(asset)?.respondentProse === "messageText"
    ? assetField(asset, "messageText")
    : assetField(asset, "name");
}

/**
 * How an author-context reader (coverage scoring) names an asset: its name.
 * Never respondent text.
 */
export function authorAssetLabel(asset: { name?: unknown }): string {
  return cleanText(asset.name);
}

/**
 * What an answer that *is* the asset (a choice label, a tested row) reads,
 * by the registry's `respondentAnswer`: the prose label, or a message's copy.
 */
export function respondentFacingAssetLabel(asset: RespondentLabelAsset): string {
  return assetFamilyOf(asset)?.respondentAnswer === "messageText"
    ? assetField(asset, "messageText")
    : respondentProseAssetLabel(asset);
}

/**
 * The exact asset answer label, with a product's typed price in the study's
 * content language (`wording`).
 */
export function respondentAnswerAssetLabel(
  asset: RespondentLabelAsset & { price?: unknown },
  wording: PriceWording,
): string {
  return productVersionLabel(respondentFacingAssetLabel(asset), asset.price, wording);
}

/**
 * How the builder's author views (a tested-asset row chip, coverage QA) name
 * an answer that is the asset: as respondents read it, but by name where
 * respondents read the prose label ("Sausage biscuit", not "Ad 2").
 */
export function authorAnswerAssetLabel(
  asset: RespondentLabelAsset & { price?: unknown },
  wording: PriceWording,
): string {
  return productVersionLabel(
    assetFamilyOf(asset)?.respondentAnswer === "messageText"
      ? assetField(asset, "messageText")
      : assetField(asset, "name"),
    asset.price,
    wording,
  );
}

/**
 * How the builder's exposure strip names an asset respondents meet by an
 * ordinal: "Ad 1: the Hamilton film", so the author sees which ordinal twins
 * read beside the name they gave it; any other asset by its name. An author
 * view only: report readers see the name (`assetCatalogTitle`).
 */
export function exposureStripAssetLabel(asset: {
  name?: unknown;
  twinFacingLabel?: unknown;
}): string {
  const ordinal = cleanText(asset.twinFacingLabel);
  const name = cleanText(asset.name);
  if (!ordinal || !name || ordinal.toLowerCase() === name.toLowerCase()) {
    return name || ordinal;
  }
  // A name that already leads with its ordinal is not prefixed twice.
  return name.toLowerCase().startsWith(`${ordinal.toLowerCase()}:`)
    ? name
    : `${ordinal}: ${name}`;
}

/**
 * The catalog title of an asset (its asset-context title, a tested row's
 * `_canonicalAsset.title`): the name, never `''` (the ordinal only when the
 * asset has no name). Readers name an asset by it, never "Ad 2: name": the
 * compiler's report copy (`reportTitle`/`reportDescription`), the report
 * charts (`formatAssetReportLabel`), the export and the twin chat.
 */
export function assetCatalogTitle(asset: {
  name?: unknown;
  twinFacingLabel?: unknown;
}): string {
  return cleanText(asset.name) || twinFacingAssetLabel(asset);
}

/**
 * The rendered label for a choice whose whole literal text is one explicit
 * asset mention. It deliberately does not search asset names: one stored ref
 * and its exact `@label` syntax are the provenance needed to project it.
 */
export function exactChoiceAssetRefAnswerLabel(
  option: { label?: unknown; assetRefs?: unknown },
  assets: ReadonlyArray<{
    id?: unknown;
    stableKey?: unknown;
    name?: unknown;
    type?: unknown;
    price?: unknown;
    description?: unknown;
    messageText?: unknown;
    twinFacingLabel?: unknown;
  }>,
  wording: PriceWording,
): string | null {
  const label = cleanText(option.label);
  const refs = Array.isArray(option.assetRefs) ? option.assetRefs : [];
  if (refs.length !== 1 || !refs[0] || typeof refs[0] !== "object") {
    return null;
  }
  const ref = refs[0] as Record<string, unknown>;
  const refLabel = cleanText(ref.label);
  const asset =
    typeof ref.assetId === "string"
      ? assets.find((candidate) => candidate.id === ref.assetId)
      : undefined;
  // The answer is its asset alone: its stored token (D2 S3.1), or the
  // `@Name` text an answer not yet stored as a token still carries.
  const isTheAsset =
    (typeof asset?.stableKey === "string" &&
      label === assetRefToken(asset.stableKey)) ||
    (refLabel !== "" && label === `@${refLabel}`);
  if (!asset || !isTheAsset) return null;
  return respondentAnswerAssetLabel(asset, wording);
}

export function resolveMaxAssetNameLength(
  maxNameLength: number | undefined,
): number {
  const resolved = maxNameLength ?? DEFAULT_MAX_ASSET_NAME_LENGTH;
  if (
    !Number.isInteger(resolved) ||
    resolved < MIN_ASSET_NAME_LENGTH ||
    resolved > MAX_PERSISTED_ASSET_NAME_LENGTH
  ) {
    throw new RangeError(
      `maxNameLength must be an integer between ${MIN_ASSET_NAME_LENGTH} and ${MAX_PERSISTED_ASSET_NAME_LENGTH}`,
    );
  }
  return resolved;
}

/**
 * Bound a display name without splitting a word when a useful natural boundary
 * exists. The full stimulus remains in the asset's description/message field.
 */
export function shortenAssetDisplayName(
  value: unknown,
  maxNameLength: number = DEFAULT_MAX_ASSET_NAME_LENGTH,
): string {
  const resolvedMaxNameLength = resolveMaxAssetNameLength(maxNameLength);
  return shortenAssetDisplayNameToLimit(value, resolvedMaxNameLength);
}

/**
 * D4 (smart asset intake): a message's name is a short label the extractor
 * writes on purpose ("Message A"), never the stimulus itself — the full body
 * lives on `messageText`/`description`. So the 150-char cap applies to every
 * asset type, message included; there is no longer a type this returns false
 * for. Kept as a function (not inlined at its call sites) so a future
 * type-specific exemption has one place to land, the way this one used to.
 */
export function shouldBoundAssetDisplayName(_type: unknown): boolean {
  return true;
}

export function shortenAssetDisplayNameToLimit(
  value: unknown,
  maxNameLength: number,
): string {
  const name = cleanText(value);
  const characters = Array.from(name);
  if (characters.length <= maxNameLength) {
    return name;
  }
  if (maxNameLength <= ELLIPSIS.length) {
    return ELLIPSIS.slice(0, maxNameLength);
  }

  const contentLimit = maxNameLength - ELLIPSIS.length;
  const prefix = characters.slice(0, contentLimit);
  const minimumNaturalBoundary = Math.floor(contentLimit / 2);
  let cutAt = -1;

  for (
    let index = prefix.length - 1;
    index >= minimumNaturalBoundary;
    index -= 1
  ) {
    if (/[.!?;:—–]/.test(prefix[index])) {
      cutAt = index;
      break;
    }
  }
  if (cutAt < 0) {
    for (let index = prefix.length - 1; index > 0; index -= 1) {
      if (/\s/.test(prefix[index])) {
        cutAt = index;
        break;
      }
    }
  }

  const candidate = (cutAt > 0 ? prefix.slice(0, cutAt) : prefix)
    .join("")
    .trimEnd()
    .replace(/[.,;:!?—–-]+$/, "")
    .trimEnd();
  const shortened = candidate || prefix.join("");
  return `${shortened}${ELLIPSIS}`;
}

/**
 * The display name the write keeps for an asset, before it is made unique:
 * trimmed and, for a bounded family, cut to the cap. The one normalisation
 * `uniquifyAssetDisplayNames` and the chat's duplicate-name refusal share.
 */
export function assetDisplayNameOnWrite(
  asset: { name?: unknown; type?: unknown },
  maxNameLength: number = DEFAULT_MAX_ASSET_NAME_LENGTH,
): string {
  return shouldBoundAssetDisplayName(asset.type)
    ? shortenAssetDisplayNameToLimit(
        asset.name,
        resolveMaxAssetNameLength(maxNameLength),
      )
    : cleanText(asset.name);
}

/**
 * A product's typed price as respondents see it, with its billing period, or
 * null: not a product, or a price the tested-asset price contract refuses. Two
 * versions are distinct at the precision they are shown (F18 cross review:
 * 29.99 and 29.994 both read "$29.99"), so the key is the one formatter's
 * output (`formatProductPrice`); "$20/month" and "$20/year" are distinct. The
 * key is typed, never a language's words.
 */
function productPriceKey(asset: {
  type?: unknown;
  price?: unknown;
}): string | null {
  if (
    asset.type !== "product" ||
    productPriceStructureIssues(asset.price).length > 0
  ) {
    return null;
  }
  const price = asset.price as ProductAssetPrice;
  return `${formatProductPrice(price)}|${price.billingPeriod ?? ""}`;
}

/**
 * Whether these assets are price versions of one product (owner decision A,
 * 2026-09-25): two or more products that share one name, letter case aside
 * after the write's normalisation, each with a valid typed price and no two
 * showing the same price (`productPriceKey`). The price is then what tells
 * them apart, so they may share the name, and an `@mention` of it names the
 * product. Typed facts only: the family, the normalised name and the typed
 * price, never the name's words.
 */
export function arePriceVersionsOfOneProduct(
  assets: ReadonlyArray<{ name?: unknown; type?: unknown; price?: unknown }>,
): boolean {
  if (assets.length < 2) {
    return false;
  }
  const names = new Set(
    assets.map((asset) => assetDisplayNameOnWrite(asset).toLowerCase()),
  );
  const prices = assets.map(productPriceKey);
  return (
    names.size === 1 &&
    !names.has("") &&
    prices.every((price) => price !== null) &&
    new Set(prices).size === prices.length
  );
}

/**
 * The asset a typed ref names in `assets`: by its id (`assetId`, else `id`),
 * then by its stable key (`assetStableKey`, else `stableKey`). The one ref
 * resolution: Nest's `resolveTestedAssetRef` reads it over the normalised
 * catalog.
 */
export function assetOfRef<T extends { id?: unknown; stableKey?: unknown }>(
  ref: unknown,
  assets: ReadonlyArray<T>,
): T | undefined {
  if (typeof ref !== "object" || ref === null || Array.isArray(ref)) {
    return undefined;
  }
  const record = ref as Record<string, unknown>;
  const byId = new Map(assets.map((asset) => [asset.id, asset]));
  const byStableKey = new Map(assets.map((asset) => [asset.stableKey, asset]));
  for (const candidate of [record.assetId, record.id].map(cleanText)) {
    if (candidate && byId.has(candidate)) return byId.get(candidate);
  }
  for (const candidate of [record.assetStableKey, record.stableKey].map(cleanText)) {
    if (candidate && byStableKey.has(candidate)) return byStableKey.get(candidate);
  }
  return undefined;
}

/**
 * The price version a question is about, when its title must say which
 * (owner decision D9, 2026-10-05): the id of the asset its one typed ref
 * names, when that asset is a price version of a product
 * (`arePriceVersionsOfOneProduct`: the versions share its name). The Save
 * binds the title's mention of the shared name to that version
 * (`restoreMentionedAssetLinks`), the compiler titles the question with its
 * price ("Standard · $14.99"), as an answer naming it reads, and the builder
 * draws the title's chip the same way. Any other question keeps decision A: a
 * shared name reads as the product's name, and a single-priced product's
 * title its name alone.
 */
export function ownPriceVersion(
  assetRefs: unknown,
  assets: ReadonlyArray<{
    id?: unknown;
    stableKey?: unknown;
    name?: unknown;
    type?: unknown;
    price?: unknown;
  }>,
): string | undefined {
  if (!Array.isArray(assetRefs) || assetRefs.length !== 1) return undefined;
  const asset = assetOfRef(assetRefs[0], assets);
  if (!asset || asset.type !== "product" || typeof asset.id !== "string") {
    return undefined;
  }
  const name = cleanText(asset.name).toLowerCase();
  const versions = assets.filter(
    (candidate) =>
      candidate.type === "product" &&
      cleanText(candidate.name).toLowerCase() === name,
  );
  return arePriceVersionsOfOneProduct(versions) ? asset.id : undefined;
}

/** One draft asset as an item reads it. */
export interface AttachTargetAsset {
  stableKey?: unknown;
  name?: unknown;
  type?: unknown;
  price?: unknown;
  media?: unknown;
}

/** Whether a draft asset already holds a file. */
export function assetHasMedia(asset: { media?: unknown }): boolean {
  return Array.isArray(asset.media) && asset.media.length > 0;
}

/**
 * The items a file can go on (F19): the draft's assets of the family, in
 * catalog order, one item per product, never one per price version. Price
 * versions of one product (`arePriceVersionsOfOneProduct`, the rule the write
 * shares their name by) are one item named by the product, and a file becomes
 * the media of every version, so the versions never differ in picture (QA3
 * PR2's confound), and it is named by the product with each version's price
 * ("96 oz bottle · $6.99, $7.49"). Any other asset is its own item, named by
 * `productVersionLabel` (its name, and its price when it has one), so every
 * item states its prices (PR-13). An item whose every asset already holds a
 * file is not offered.
 */
export function attachItemsOf(
  family: string,
  assets: ReadonlyArray<AttachTargetAsset>,
  wording: PriceWording,
): Array<ChatMessageCollectAttachItem> {
  const members = assets.filter(
    (asset): asset is AttachTargetAsset & { stableKey: string } =>
      asset.type === family &&
      typeof asset.stableKey === "string" &&
      asset.stableKey.length > 0,
  );
  const byName = new Map<string, Array<(typeof members)[number]>>();
  for (const asset of members) {
    const name = assetDisplayNameOnWrite(asset).toLowerCase();
    byName.set(name, [...(byName.get(name) ?? []), asset]);
  }
  const items: Array<{
    item: ChatMessageCollectAttachItem;
    assets: Array<(typeof members)[number]>;
  }> = [];
  const placed = new Set<string>();
  for (const asset of members) {
    if (placed.has(asset.stableKey)) continue;
    const sharing = byName.get(assetDisplayNameOnWrite(asset).toLowerCase())!;
    const versions = arePriceVersionsOfOneProduct(sharing) ? sharing : [asset];
    versions.forEach((version) => placed.add(version.stableKey));
    const name = assetDisplayNameOnWrite(asset) || asset.stableKey;
    items.push({
      item: {
        label:
          versions.length > 1
            ? `${name.trim()} · ${versions
                .map((version) =>
                  formatTypedPrice(version.price as ProductAssetPrice, wording),
                )
                .join(", ")}`
            : productVersionLabel(name, asset.price, wording),
        assetKeys: versions.map((version) => version.stableKey),
      },
      assets: versions,
    });
  }
  return items
    .filter(({ assets: versions }) => !versions.every(assetHasMedia))
    .map(({ item }) => item);
}

/** A request part as the upload card reads it: its file and its capability. */
interface CardPart {
  material?: { assetType: string; assetKeys: ReadonlyArray<string> } | null;
  capability?: CapabilityId | null;
}

/**
 * Whether the upload card asks for a part's file (L-B2): a part names a file
 * the user has (`material`) and needs no capability, or one whose file the
 * card takes (the registry's typed `fileOnCard`: BP-01's swap). A part whose
 * capability has nowhere to put the file (CT-13's line-up shot, PR-13's
 * packshots past the product cap) is asked on no card. The card
 * (`materialCardOf`) and the list beside it (`partsListedBesideCard`) read
 * this one rule.
 */
function cardAsksFor(part: CardPart): boolean {
  return Boolean(
    part.material && (!part.capability || capabilityFileOnCard(part.capability)),
  );
}

/**
 * The upload card a reply carries for a file the user has (B2): from the
 * model's typed request parts the card asks for (`cardAsksFor`, PLAN's or a
 * revision stage's `material`) and the catalog the turn leaves, never from
 * the user's words. One card per reply, for the first such part's family:
 * - its items without a file (`attachItemsOf`): those of the assets the
 *   parts name, or every one when a part names none;
 * - else, when a part names none, a card for new ones of a family a file
 *   alone can add to the study (`materialOffers`' `addAs`: one of the study's
 *   families whose `materials` take media, with room left under its
 *   `maxPerStudy`), so a message test is never offered an ad (MT-22); it is
 *   optional, so the user passes it in one click.
 * Null when no part asks for a file, or nothing is left to ask it for.
 */
export function materialCardOf(
  parts: ReadonlyArray<CardPart>,
  study: {
    families: ReadonlyArray<TestedAssetType> | null;
    assets: ReadonlyArray<AttachTargetAsset>;
  },
  wording: PriceWording,
): ChatMessageCollect | null {
  const requests = parts.flatMap((part) =>
    cardAsksFor(part) && part.material ? [part.material] : [],
  );
  const family = requests[0]?.assetType;
  if (!isAssetFamily(family)) return null;
  const ofFamily = requests.filter((request) => request.assetType === family);
  const named = new Set(ofFamily.flatMap((request) => request.assetKeys));
  const everyItem = ofFamily.some((request) => request.assetKeys.length === 0);
  const items = attachItemsOf(family, study.assets, wording).filter(
    (item) => everyItem || item.assetKeys.some((key) => named.has(key)),
  );
  if (items.length > 0) {
    return {
      kind: "upload",
      assetType: family,
      maxAssets: items.length * FILES_PER_ASSET,
      attachTo: items,
    };
  }
  const added = everyItem
    ? materialOffers("media", TESTED_ASSET_MAX_BY_TYPE[family], study, wording)
        .addAs.find((offer) => offer.family === family)
    : undefined;
  return added
    ? { kind: "upload", assetType: family, maxAssets: added.room, optional: true }
    : null;
}

/**
 * The parts of a request a reply lists under its text, beside the card it
 * carries (B2, B1b): a part the card asks for (`cardAsksFor`) is asked there
 * instead, unless it also needs a capability, whose reason the list keeps
 * (BP-01's "show both photos"). A part asked on no card is listed. The
 * builder's lines (`notDoneLines`) and the reply writer's request read this
 * one rule.
 */
export function partsListedBesideCard<Part extends CardPart>(
  parts: ReadonlyArray<Part>,
  carriesCard: boolean,
): Array<Part> {
  return parts.filter((part) => !(carriesCard && cardAsksFor(part) && !part.capability));
}

/**
 * The parts a first build reports once its catalog is known: a part for a
 * file the user has (`material`) that names assets which each already hold
 * their file asks for nothing, as the files the user gave before the Build
 * are those (gate 2026-10-15, LP-14: "I couldn't do this: Full-page
 * screenshots of all three", attached on the card before the Build). A part
 * that names none asks for new assets, which the card's room decides
 * (`materialCardOf`); one that needs a capability keeps its reason.
 */
export function partsAskingOnBuild<
  Part extends {
    material?: { assetKeys: ReadonlyArray<string> } | null;
    capability?: unknown;
  },
>(parts: ReadonlyArray<Part>, assets: ReadonlyArray<AttachTargetAsset>): Array<Part> {
  const held = new Set(
    assets.flatMap((asset) =>
      typeof asset.stableKey === "string" && assetHasMedia(asset) ? [asset.stableKey] : [],
    ),
  );
  return parts.filter(
    (part) =>
      !part.material ||
      Boolean(part.capability) ||
      part.material.assetKeys.length === 0 ||
      !part.material.assetKeys.every((key) => held.has(key)),
  );
}

/** An item a file can go on, with the family its assets belong to. */
export interface MaterialAttachOffer {
  family: TestedAssetType;
  item: ChatMessageCollectAttachItem;
}

/** What a file or a link handed to the builder on its own can become. */
export interface MaterialOffers {
  /**
   * The families a new asset can be made as, with how many of what was given
   * each has room for under its `maxPerStudy` (at least one): those whose
   * registry material takes it, the study's own family alone when it does.
   */
  addAs: Array<{ family: TestedAssetType; room: number }>;
  /** Of those families, the ones already holding their `maxPerStudy`. */
  atLimit: Array<TestedAssetType>;
  /**
   * The study's items a respondent is shown (`exposureBound`) that have no
   * file yet (`attachItemsOf`), in catalog order: each takes `FILES_PER_ASSET` (F19).
   */
  attachTo: Array<MaterialAttachOffer>;
}

/**
 * The families a study uses, which a file or a link handed over on its own
 * may become (owner decision B2-4): the families its recipe declares as its
 * material (`materials`; before the first build the goal's recipe, as the
 * StudySpec resolver reads it), with any the study's catalog declares
 * (`testedAssets.assetTypes`). An open recipe (explore), or a study with no
 * recipe, uses the families its catalog declares, and any family while it
 * declares none (null).
 */
export function studyMaterialFamilies(study: {
  recipe: { recipeId: string; recipeVersion: string } | null;
  declaredTypes: ReadonlyArray<string>;
}): ReadonlyArray<TestedAssetType> | null {
  const materials = study.recipe
    ? recipeDeclarations(study.recipe)?.materials
    : undefined;
  const declared = study.declaredTypes.filter(isAssetFamily);
  if (!Array.isArray(materials)) return declared.length > 0 ? declared : null;
  return [
    ...new Set([
      ...materials.map((material) => material.family).filter(isAssetFamily),
      ...declared,
    ]),
  ];
}

/**
 * What the builder offers a file (`media`) or a link (`link`) handed over on
 * its own, from the family registry's typed facts. One rule for both cards:
 *
 * - Add it as a new asset of one of the study's families
 *   (`studyMaterialFamilies`) whose `materials` take it, so a file or a link
 *   never makes a family the study does not use (owner decision B2-4). A
 *   study whose families do not take it (a brand, pain, claim, concept or
 *   product study is written, never made from a file or a link) is offered
 *   none: its words make the item, and the model asks for the file. A study
 *   that uses any family (null) is offered every family that takes it. A
 *   family is offered for as many as it has room for; one already at its
 *   maximum is not offered.
 * - Put it on an existing item a respondent is shown that has no file yet
 *   (a concept, a product, a message, a page written as copy), each item
 *   taking the registry's `filesPerAsset`, by the attach mechanism
 *   (`collect.attachTo`, F19).
 */
export function materialOffers(
  material: "media" | "link",
  count: number,
  study: {
    families: ReadonlyArray<TestedAssetType> | null;
    assets: ReadonlyArray<AttachTargetAsset>;
  },
  wording: PriceWording,
): MaterialOffers {
  const taking =
    material === "media" ? MEDIA_MATERIAL_FAMILIES : LINK_MATERIAL_FAMILIES;
  const families =
    study.families === null
      ? taking
      : taking.filter((family) => study.families!.includes(family));
  const roomOf = (family: TestedAssetType) =>
    Math.min(
      count,
      TESTED_ASSET_MAX_BY_TYPE[family] -
        study.assets.filter((asset) => asset.type === family).length,
    );
  return {
    addAs: families
      .map((family) => ({ family, room: roomOf(family) }))
      .filter(({ room }) => room > 0),
    atLimit: families.filter((family) => roomOf(family) <= 0),
    attachTo: EXPOSURE_BOUND_FAMILIES.flatMap((family) =>
      attachItemsOf(family, study.assets, wording).map((item) => ({
        family,
        item,
      })),
    ),
  };
}
